import { useEffect, useRef, useState } from "react";
import { Image, Send, X, FileText } from "lucide-react";
import EmojiPicker from "./EmojiPicker";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_MESSAGE_LENGTH = 4000;
const ALLOWED_FILE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "application/zip",
  "application/x-zip-compressed",
  "application/vnd.rar",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
  "application/octet-stream",
  "video/mp4",
  "video/webm",
  "video/quicktime",
]);
const ALLOWED_FILE_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "webp", "gif", "heic", "heif",
  "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx",
  "txt", "csv", "zip", "rar", "7z",
  "mp4", "webm", "mov",
]);

function getFileExtension(name) {
  const normalized = String(name || "").trim().toLowerCase();
  const parts = normalized.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

function validateAttachment(file) {
  if (!file) return "";
  const type = String(file.type || "").trim().toLowerCase();
  const extension = getFileExtension(file.name);
  const isAllowedByType = type && ALLOWED_FILE_TYPES.has(type);
  const isAllowedByExtension = extension && ALLOWED_FILE_EXTENSIONS.has(extension);

  if (!isAllowedByType && !isAllowedByExtension) {
    return "Unsupported file type. Use images, videos, PDF, Office docs, text, CSV, ZIP, RAR, or 7z files.";
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return "Attachment must be 10 MB or smaller.";
  }
  return "";
}

export default function MessageInput({
  onSend,
  sending,
  disabled,
  placeholder = "Type a message...",
  disabledPlaceholder = "Select a conversation",
  accept = "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar-compressed,application/x-7z-compressed,application/octet-stream,video/mp4,video/webm,video/quicktime,.mov,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z",
}) {
  const [draft, setDraft] = useState("");
  const [filePreview, setFilePreview] = useState(null); // { file, objectUrl, type }
  const [localError, setLocalError] = useState("");
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => () => {
    if (filePreview?.objectUrl) {
      URL.revokeObjectURL(filePreview.objectUrl);
    }
  }, [filePreview]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, [draft]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateAttachment(file);
    if (validationError) {
      setLocalError(validationError);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (filePreview?.objectUrl) {
      URL.revokeObjectURL(filePreview.objectUrl);
    }

    const objectUrl = URL.createObjectURL(file);
    setLocalError("");
    setFilePreview({ file, objectUrl, type: file.type });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearFile = () => {
    if (filePreview?.objectUrl) URL.revokeObjectURL(filePreview.objectUrl);
    setFilePreview(null);
    setLocalError("");
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (disabled || sending || (!text && !filePreview)) return;
    if (text.length > MAX_MESSAGE_LENGTH) {
      setLocalError(`Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.`);
      return;
    }
    setLocalError("");
    const result = await onSend(text, filePreview?.file || null);
    if (result === false || result === null) return;
    setDraft("");
    if (filePreview?.objectUrl) URL.revokeObjectURL(filePreview.objectUrl);
    setFilePreview(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !sending && !disabled && (draft.trim().length > 0 || Boolean(filePreview));

  return (
    <div className="z-10 shrink-0 border-t border-neutral-100 bg-white/95 backdrop-blur dark:border-neutral-800 dark:bg-neutral-900/95">
      {filePreview && (
        <div className="px-3 pb-1 pt-3 sm:px-4">
          <div className="flex items-center gap-3 overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800/80">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900 sm:h-12 sm:w-12">
            {filePreview.type.startsWith("image/") ? (
              <img
                src={filePreview.objectUrl}
                alt="preview"
                className="h-full w-full object-cover"
              />
            ) : filePreview.type.startsWith("video/") ? (
              <div className="relative h-full w-full">
                <video
                  src={filePreview.objectUrl}
                  className="h-full w-full object-cover"
                  controls
                  playsInline
                  preload="metadata"
                />
              </div>
            ) : (
              <FileText size={20} className="text-neutral-500" />
            )}
            <button
              type="button"
              onClick={clearFile}
              className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 text-white shadow"
              aria-label="Remove file"
            >
              <X size={9} />
            </button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-neutral-700 dark:text-neutral-200">
                {filePreview.file.name}
              </p>
              <p className="mt-0.5 truncate text-[11px] text-neutral-500 dark:text-neutral-400">
                {filePreview.file.type || "Attachment"} · {formatBytes(filePreview.file.size)}
              </p>
            </div>
          </div>
        </div>
      )}

      {localError ? (
        <div className="px-3 pt-3 text-[11px] text-rose-600 dark:text-rose-300 sm:px-4">
          {localError}
        </div>
      ) : null}

      <div className="flex items-end gap-1 px-2 py-2 pb-[calc(0.65rem+env(safe-area-inset-bottom))] sm:gap-2 sm:px-3 sm:py-2.5">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-40 dark:hover:bg-neutral-800 sm:h-10 sm:w-10"
          aria-label="Attach file"
        >
          <Image size={16} />
        </button>

        <div className="shrink-0">
          <EmojiPicker onSelect={(emoji) => setDraft((prev) => prev + emoji)} position="top" />
        </div>

        <div className="min-w-0 flex-1 rounded-[1.25rem] border border-neutral-200 bg-neutral-50 px-3 py-2 transition focus-within:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:focus-within:border-neutral-500 sm:rounded-[1.4rem]">
          <textarea
            ref={textareaRef}
            data-message-composer-input="true"
            rows={1}
            value={draft}
            onChange={(e) => {
              if (e.target.value.length > MAX_MESSAGE_LENGTH) {
                setLocalError(`Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.`);
                return;
              }
              setLocalError((current) => (
                current === `Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.` ? "" : current
              ));
              setDraft(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={disabled ? disabledPlaceholder : placeholder}
            maxLength={MAX_MESSAGE_LENGTH}
            className="max-h-[112px] min-h-[22px] w-full resize-none overflow-y-auto bg-transparent text-[13px] leading-5 text-neutral-900 outline-none placeholder:text-neutral-400 disabled:opacity-50 dark:text-neutral-100 dark:placeholder:text-neutral-500 sm:max-h-[120px] sm:leading-6"
          />
        </div>

        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white transition hover:bg-neutral-800 disabled:opacity-40 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200 sm:h-11 sm:w-11"
          aria-label="Send message"
        >
          {sending ? (
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent dark:border-neutral-900" />
          ) : (
            <Send size={16} />
          )}
        </button>
      </div>
    </div>
  );
}
